import React, { useState, useEffect, useRef } from 'react';
import { 
  FaChartLine, 
  FaUsers, 
  FaUserCheck, 
  FaClock, 
  FaMoneyBillWave, 
  FaArrowUp, 
  FaArrowDown, 
  FaFilter,
  FaDownload,
  FaCalendarAlt,
  FaEye,
  FaCog,
  FaSync,
  FaChartBar,
  FaTable,
  FaMapMarkerAlt,
  FaGenderless,
  FaBirthdayCake,
  FaGraduationCap,
  FaBriefcase,
  FaBuilding
} from 'react-icons/fa';
import './HRAnalytics.css';

const HRAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [viewType, setViewType] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [metrics, setMetrics] = useState(null);

  // Mock data - Replace with actual API calls
  const mockMetrics = {
    overview: {
      totalEmployees: 1247,
      activeEmployees: 1189,
      newHires: 45,
      turnover: 23,
      avgTenure: '2.8 years',
      engagementScore: 78,
      productivityScore: 82,
      diversityIndex: 64
    },
    trends: {
      hiring: [45, 52, 48, 61, 55, 58, 67, 71, 65, 59, 63, 70],
      turnover: [18, 22, 19, 25, 23, 20, 28, 26, 24, 21, 23, 25],
      engagement: [72, 74, 76, 75, 78, 77, 79, 81, 80, 78, 82, 84],
      productivity: [75, 77, 79, 78, 80, 82, 81, 83, 85, 84, 86, 88]
    },
    demographics: {
      gender: { male: 58, female: 39, other: 3 },
      ageGroups: { '18-25': 15, '26-35': 42, '36-45': 28, '46-55': 12, '56+': 3 },
      departments: { engineering: 35, sales: 22, marketing: 15, hr: 8, operations: 20 },
      locations: { 'North America': 45, 'Europe': 32, 'Asia': 18, 'Other': 5 },
      education: { bachelor: 45, master: 38, doctorate: 12, other: 5 }
    },
    performance: {
      highPerformers: 28,
      meetingExpectations: 65,
      needsImprovement: 7,
      avgPerformanceScore: 4.2,
      topSkills: ['Leadership', 'Technical', 'Communication', 'Problem Solving', 'Teamwork'],
      trainingCompletion: 87
    },
    costs: {
      totalCompensation: 18500000,
      avgSalary: 85000,
      trainingCost: 450000,
      recruitmentCost: 320000,
      turnoverCost: 1250000,
      benefitsCost: 3700000
    }
  };

  useEffect(() => {
    loadMetrics();
  }, [timeRange, departmentFilter]);

  const loadMetrics = async () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setMetrics(mockMetrics);
      setIsLoading(false);
    }, 1000);
  };

  const exportReport = () => {
    // Implement export functionality
    console.log('Exporting analytics report...');
  };

  const calculateChange = (current, previous) => {
    if (previous === 0) return 100;
    return ((current - previous) / previous) * 100;
  };

  const MetricCard = ({ title, value, change, icon, color }) => (
    <div className="hr-analytics-metric-card">
      <div className="hr-analytics-metric-header">
        <div className={`hr-analytics-metric-icon hr-analytics-metric-icon-${color}`}>
          {icon}
        </div>
        <div className="hr-analytics-metric-info">
          <h3 className="hr-analytics-metric-title">{title}</h3>
          <div className="hr-analytics-metric-value">{value}</div>
          {change && (
            <div className={`hr-analytics-metric-change ${change >= 0 ? 'positive' : 'negative'}`}>
              {change >= 0 ? <FaArrowUp /> : <FaArrowDown />}
              {Math.abs(change).toFixed(1)}%
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const ChartContainer = ({ title, children, actions }) => (
    <div className="hr-analytics-chart-card">
      <div className="hr-analytics-chart-header">
        <h3 className="hr-analytics-chart-title">{title}</h3>
        {actions && (
          <div className="hr-analytics-chart-actions">
            {actions}
          </div>
        )}
      </div>
      <div className="hr-analytics-chart-content">
        {children}
      </div>
    </div>
  );

  if (!metrics) {
    return (
      <div className="hr-analytics-loading">
        <div className="hr-analytics-loading-spinner"></div>
        <p>Loading HR Analytics...</p>
      </div>
    );
  }

  return (
    <div className="hr-analytics">
      {/* Header */}
      <div className="hr-analytics-header">
        <div className="hr-analytics-header-content">
          <div className="hr-analytics-title-section">
            <h1 className="hr-analytics-title">
              <FaChartLine className="hr-analytics-title-icon" />
              HR Analytics Dashboard
            </h1>
            <p className="hr-analytics-subtitle">
              Comprehensive insights into your workforce metrics and trends
            </p>
          </div>
          <div className="hr-analytics-controls">
            <div className="hr-analytics-filter-group">
              <select 
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="hr-analytics-filter"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="1y">Last Year</option>
              </select>
              
              <select 
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="hr-analytics-filter"
              >
                <option value="all">All Departments</option>
                <option value="engineering">Engineering</option>
                <option value="sales">Sales</option>
                <option value="marketing">Marketing</option>
                <option value="hr">HR</option>
                <option value="operations">Operations</option>
              </select>
            </div>
            
            <div className="hr-analytics-action-group">
              <button 
                className="hr-analytics-action-btn"
                onClick={loadMetrics}
                disabled={isLoading}
              >
                <FaSync className={isLoading ? 'spin' : ''} />
                Refresh
              </button>
              
              <button 
                className="hr-analytics-action-btn hr-analytics-action-btn-primary"
                onClick={exportReport}
              >
                <FaDownload />
                Export Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="hr-analytics-tabs">
        <button 
          className={`hr-analytics-tab ${viewType === 'overview' ? 'active' : ''}`}
          onClick={() => setViewType('overview')}
        >
          <FaChartBar />
          Overview
        </button>
        <button 
          className={`hr-analytics-tab ${viewType === 'demographics' ? 'active' : ''}`}
          onClick={() => setViewType('demographics')}
        >
          <FaUsers />
          Demographics
        </button>
        <button 
          className={`hr-analytics-tab ${viewType === 'performance' ? 'active' : ''}`}
          onClick={() => setViewType('performance')}
        >
          <FaUserCheck />
          Performance
        </button>
        <button 
          className={`hr-analytics-tab ${viewType === 'costs' ? 'active' : ''}`}
          onClick={() => setViewType('costs')}
        >
          <FaMoneyBillWave />
          Cost Analysis
        </button>
        <button 
          className={`hr-analytics-tab ${viewType === 'trends' ? 'active' : ''}`}
          onClick={() => setViewType('trends')}
        >
          <FaChartLine />
          Trends
        </button>
      </div>

      {/* Main Content */}
      <div className="hr-analytics-content">
        {viewType === 'overview' && (
          <>
            {/* Key Metrics Grid */}
            <div className="hr-analytics-metrics-grid">
              <MetricCard
                title="Total Employees"
                value={metrics.overview.totalEmployees.toLocaleString()}
                change={4.2}
                icon={<FaUsers />}
                color="blue"
              />
              <MetricCard
                title="Active Employees"
                value={metrics.overview.activeEmployees.toLocaleString()}
                change={2.8}
                icon={<FaUserCheck />}
                color="green"
              />
              <MetricCard
                title="New Hires"
                value={metrics.overview.newHires}
                change={12.5}
                icon={<FaBriefcase />}
                color="purple"
              />
              <MetricCard
                title="Turnover Rate"
                value={`${((metrics.overview.turnover / metrics.overview.totalEmployees) * 100).toFixed(1)}%`}
                change={-3.2}
                icon={<FaClock />}
                color="orange"
              />
              <MetricCard
                title="Engagement Score"
                value={`${metrics.overview.engagementScore}/100`}
                change={5.1}
                icon={<FaChartLine />}
                color="teal"
              />
              <MetricCard
                title="Avg Tenure"
                value={metrics.overview.avgTenure}
                change={8.7}
                icon={<FaCalendarAlt />}
                color="indigo"
              />
            </div>

            {/* Charts Row */}
            <div className="hr-analytics-charts-row">
              <ChartContainer 
                title="Headcount Trend"
                actions={
                  <button className="hr-analytics-chart-action">
                    <FaEye />
                  </button>
                }
              >
                <div className="hr-analytics-chart-placeholder">
                  <p>Headcount Trend Chart</p>
                  <div className="hr-analytics-chart-bars">
                    {metrics.trends.hiring.map((value, index) => (
                      <div 
                        key={index}
                        className="hr-analytics-chart-bar"
                        style={{ height: `${(value / 80) * 100}%` }}
                        title={`Month ${index + 1}: ${value}`}
                      ></div>
                    ))}
                  </div>
                </div>
              </ChartContainer>

              <ChartContainer 
                title="Department Distribution"
                actions={
                  <button className="hr-analytics-chart-action">
                    <FaCog />
                  </button>
                }
              >
                <div className="hr-analytics-chart-placeholder">
                  <p>Department Distribution Chart</p>
                  <div className="hr-analytics-pie-chart">
                    {Object.entries(metrics.demographics.departments).map(([dept, percentage], index) => (
                      <div 
                        key={dept}
                        className="hr-analytics-pie-segment"
                        style={{
                          backgroundColor: `hsl(${index * 60}, 70%, 60%)`,
                          transform: `rotate(${index * 72}deg)`
                        }}
                        title={`${dept}: ${percentage}%`}
                      ></div>
                    ))}
                  </div>
                </div>
              </ChartContainer>
            </div>
          </>
        )}

        {viewType === 'demographics' && (
          <div className="hr-analytics-demographics">
            <div className="hr-analytics-demo-grid">
              <ChartContainer title="Gender Distribution">
                <div className="hr-analytics-demo-chart">
                  {Object.entries(metrics.demographics.gender).map(([gender, percentage]) => (
                    <div key={gender} className="hr-analytics-demo-item">
                      <div className="hr-analytics-demo-label">
                        <FaGenderless />
                        {gender.charAt(0).toUpperCase() + gender.slice(1)}
                      </div>
                      <div className="hr-analytics-demo-bar">
                        <div 
                          className="hr-analytics-demo-fill"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="hr-analytics-demo-value">{percentage}%</div>
                    </div>
                  ))}
                </div>
              </ChartContainer>

              <ChartContainer title="Age Groups">
                <div className="hr-analytics-demo-chart">
                  {Object.entries(metrics.demographics.ageGroups).map(([ageGroup, percentage]) => (
                    <div key={ageGroup} className="hr-analytics-demo-item">
                      <div className="hr-analytics-demo-label">
                        <FaBirthdayCake />
                        {ageGroup}
                      </div>
                      <div className="hr-analytics-demo-bar">
                        <div 
                          className="hr-analytics-demo-fill"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="hr-analytics-demo-value">{percentage}%</div>
                    </div>
                  ))}
                </div>
              </ChartContainer>

              <ChartContainer title="Education Levels">
                <div className="hr-analytics-demo-chart">
                  {Object.entries(metrics.demographics.education).map(([education, percentage]) => (
                    <div key={education} className="hr-analytics-demo-item">
                      <div className="hr-analytics-demo-label">
                        <FaGraduationCap />
                        {education.charAt(0).toUpperCase() + education.slice(1)}
                      </div>
                      <div className="hr-analytics-demo-bar">
                        <div 
                          className="hr-analytics-demo-fill"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="hr-analytics-demo-value">{percentage}%</div>
                    </div>
                  ))}
                </div>
              </ChartContainer>

              <ChartContainer title="Geographic Distribution">
                <div className="hr-analytics-demo-chart">
                  {Object.entries(metrics.demographics.locations).map(([location, percentage]) => (
                    <div key={location} className="hr-analytics-demo-item">
                      <div className="hr-analytics-demo-label">
                        <FaMapMarkerAlt />
                        {location}
                      </div>
                      <div className="hr-analytics-demo-bar">
                        <div 
                          className="hr-analytics-demo-fill"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="hr-analytics-demo-value">{percentage}%</div>
                    </div>
                  ))}
                </div>
              </ChartContainer>
            </div>
          </div>
        )}

        {viewType === 'performance' && (
          <div className="hr-analytics-performance">
            <div className="hr-analytics-perf-grid">
              <ChartContainer title="Performance Distribution">
                <div className="hr-analytics-perf-distribution">
                  <div className="hr-analytics-perf-category">
                    <div className="hr-analytics-perf-label">High Performers</div>
                    <div className="hr-analytics-perf-bar">
                      <div 
                        className="hr-analytics-perf-fill hr-analytics-perf-high"
                        style={{ width: `${metrics.performance.highPerformers}%` }}
                      ></div>
                    </div>
                    <div className="hr-analytics-perf-value">{metrics.performance.highPerformers}%</div>
                  </div>
                  <div className="hr-analytics-perf-category">
                    <div className="hr-analytics-perf-label">Meeting Expectations</div>
                    <div className="hr-analytics-perf-bar">
                      <div 
                        className="hr-analytics-perf-fill hr-analytics-perf-medium"
                        style={{ width: `${metrics.performance.meetingExpectations}%` }}
                      ></div>
                    </div>
                    <div className="hr-analytics-perf-value">{metrics.performance.meetingExpectations}%</div>
                  </div>
                  <div className="hr-analytics-perf-category">
                    <div className="hr-analytics-perf-label">Needs Improvement</div>
                    <div className="hr-analytics-perf-bar">
                      <div 
                        className="hr-analytics-perf-fill hr-analytics-perf-low"
                        style={{ width: `${metrics.performance.needsImprovement}%` }}
                      ></div>
                    </div>
                    <div className="hr-analytics-perf-value">{metrics.performance.needsImprovement}%</div>
                  </div>
                </div>
              </ChartContainer>

              <ChartContainer title="Top Skills">
                <div className="hr-analytics-skills-list">
                  {metrics.performance.topSkills.map((skill, index) => (
                    <div key={skill} className="hr-analytics-skill-item">
                      <div className="hr-analytics-skill-rank">#{index + 1}</div>
                      <div className="hr-analytics-skill-name">{skill}</div>
                      <div className="hr-analytics-skill-bar">
                        <div 
                          className="hr-analytics-skill-fill"
                          style={{ width: `${90 - (index * 15)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </ChartContainer>

              <ChartContainer title="Training Completion">
                <div className="hr-analytics-training-metric">
                  <div className="hr-analytics-training-value">
                    {metrics.performance.trainingCompletion}%
                  </div>
                  <div className="hr-analytics-training-label">
                    Overall Training Completion Rate
                  </div>
                  <div className="hr-analytics-training-progress">
                    <div 
                      className="hr-analytics-training-fill"
                      style={{ width: `${metrics.performance.trainingCompletion}%` }}
                    ></div>
                  </div>
                </div>
              </ChartContainer>
            </div>
          </div>
        )}

        {viewType === 'costs' && (
          <div className="hr-analytics-costs">
            <div className="hr-analytics-costs-grid">
              <MetricCard
                title="Total Compensation"
                value={`$${(metrics.costs.totalCompensation / 1000000).toFixed(1)}M`}
                change={6.8}
                icon={<FaMoneyBillWave />}
                color="blue"
              />
              <MetricCard
                title="Average Salary"
                value={`$${metrics.costs.avgSalary.toLocaleString()}`}
                change={3.2}
                icon={<FaUserCheck />}
                color="green"
              />
              <MetricCard
                title="Training Cost"
                value={`$${(metrics.costs.trainingCost / 1000).toFixed(0)}K`}
                change={-2.1}
                icon={<FaGraduationCap />}
                color="purple"
              />
              <MetricCard
                title="Recruitment Cost"
                value={`$${(metrics.costs.recruitmentCost / 1000).toFixed(0)}K`}
                change={8.5}
                icon={<FaBriefcase />}
                color="orange"
              />
              <MetricCard
                title="Turnover Cost"
                value={`$${(metrics.costs.turnoverCost / 1000000).toFixed(1)}M`}
                change={-12.3}
                icon={<FaClock />}
                color="red"
              />
              <MetricCard
                title="Benefits Cost"
                value={`$${(metrics.costs.benefitsCost / 1000000).toFixed(1)}M`}
                change={4.7}
                icon={<FaBuilding />}
                color="teal"
              />
            </div>

            <ChartContainer title="Cost Distribution">
              <div className="hr-analytics-cost-breakdown">
                {Object.entries({
                  'Compensation': metrics.costs.totalCompensation,
                  'Benefits': metrics.costs.benefitsCost,
                  'Training': metrics.costs.trainingCost,
                  'Recruitment': metrics.costs.recruitmentCost,
                  'Turnover': metrics.costs.turnoverCost
                }).map(([category, amount]) => (
                  <div key={category} className="hr-analytics-cost-item">
                    <div className="hr-analytics-cost-category">{category}</div>
                    <div className="hr-analytics-cost-amount">
                      ${(amount / 1000000).toFixed(1)}M
                    </div>
                    <div className="hr-analytics-cost-bar">
                      <div 
                        className="hr-analytics-cost-fill"
                        style={{ 
                          width: `${(amount / metrics.costs.totalCompensation) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </ChartContainer>
          </div>
        )}

        {viewType === 'trends' && (
          <div className="hr-analytics-trends">
            <div className="hr-analytics-trends-grid">
              <ChartContainer title="Hiring Trends">
                <div className="hr-analytics-trend-chart">
                  <div className="hr-analytics-trend-bars">
                    {metrics.trends.hiring.map((value, index) => (
                      <div key={index} className="hr-analytics-trend-bar-container">
                        <div 
                          className="hr-analytics-trend-bar hr-analytics-trend-bar-hiring"
                          style={{ height: `${(value / 80) * 100}%` }}
                          title={`Month ${index + 1}: ${value} hires`}
                        ></div>
                        <div className="hr-analytics-trend-label">{index + 1}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </ChartContainer>

              <ChartContainer title="Turnover Trends">
                <div className="hr-analytics-trend-chart">
                  <div className="hr-analytics-trend-bars">
                    {metrics.trends.turnover.map((value, index) => (
                      <div key={index} className="hr-analytics-trend-bar-container">
                        <div 
                          className="hr-analytics-trend-bar hr-analytics-trend-bar-turnover"
                          style={{ height: `${(value / 30) * 100}%` }}
                          title={`Month ${index + 1}: ${value} turnover`}
                        ></div>
                        <div className="hr-analytics-trend-label">{index + 1}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </ChartContainer>

              <ChartContainer title="Engagement Trends">
                <div className="hr-analytics-trend-chart">
                  <div className="hr-analytics-trend-line">
                    {metrics.trends.engagement.map((value, index) => (
                      <div 
                        key={index}
                        className="hr-analytics-trend-point"
                        style={{ 
                          left: `${(index / 11) * 100}%`,
                          bottom: `${value}%`
                        }}
                        title={`Month ${index + 1}: ${value}%`}
                      ></div>
                    ))}
                  </div>
                </div>
              </ChartContainer>

              <ChartContainer title="Productivity Trends">
                <div className="hr-analytics-trend-chart">
                  <div className="hr-analytics-trend-line">
                    {metrics.trends.productivity.map((value, index) => (
                      <div 
                        key={index}
                        className="hr-analytics-trend-point"
                        style={{ 
                          left: `${(index / 11) * 100}%`,
                          bottom: `${value}%`
                        }}
                        title={`Month ${index + 1}: ${value}%`}
                      ></div>
                    ))}
                  </div>
                </div>
              </ChartContainer>
            </div>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default HRAnalytics;